/**
 * ChatBubble
 *
 * Renders one chat message — user (right-aligned, primary bg) or
 * assistant (left-aligned, secondary bg). Accepts an optional
 * `avatar` slot so Nutri, NutriKnows, and the portal bot can each
 * supply their own icon without forking the bubble logic.
 *
 * Markdown is rendered for assistant messages via react-markdown.
 * User messages are plain text to avoid accidental injection risks.
 */

import React from 'react';
import ReactMarkdown from 'react-markdown';

export type ChatRole = 'user' | 'assistant';

export type ChatBubbleProps = {
  role: ChatRole;
  content: string;
  /** Icon shown next to assistant bubbles. Omit to hide the avatar. */
  avatar?: React.ReactNode;
  /** Extra class added to the bubble `div`. */
  className?: string;
};

export function ChatBubble({ role, content, avatar, className = '' }: ChatBubbleProps) {
  const isUser = role === 'user';

  return (
    <div className={`flex gap-2 ${isUser ? 'justify-end' : 'justify-start'}`}>
      {!isUser && avatar && (
        <div className="shrink-0 mt-0.5">{avatar}</div>
      )}
      <div
        className={`max-w-[85%] rounded-2xl text-sm leading-relaxed px-3.5 py-2.5 ${
          isUser
            ? 'bg-primary text-white rounded-br-sm'
            : 'bg-secondary text-foreground rounded-bl-sm border border-border/40'
        } ${className}`}
      >
        {isUser ? (
          content
        ) : (
          <div className="prose prose-sm max-w-none prose-p:mb-1 prose-p:leading-relaxed prose-headings:text-sm">
            <ReactMarkdown>{content}</ReactMarkdown>
          </div>
        )}
      </div>
    </div>
  );
}
